import { RichText, slateToHtml, TextInput } from '@amelio/ui-library';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import GenerateObjectiveAiButton from './GenerateObjectiveAiButton';
import ObjectiveCompetenciesField from './ObjectiveCompetenciesField';
import { useFormErrorMessage } from '../../hooks/useFormErrorMessage';
import { DESCRIPTION_MAX_LENGTH, TITLE_MAX_LENGTH } from '../../validation';
import SectionHeading from '../SectionHeading';
import { GlassFrame, WhitePanel } from '../surfaces';

import type { ObjectiveFormData } from '../../validation';
import type { htmlToSlate } from '@amelio/ui-library';
import type { Ref } from 'react';
import type { Control } from 'react-hook-form';

import { EDITOR_TOOLBAR_DENSITY } from '@/constants/RichTextEditor';
import i18n from '@/plugins/i18n';

/** The host's top bar is fixed above the scrollport and would otherwise cover what we scroll to. */
const HOST_TOP_BAR_HEIGHT = 64;
const TITLE_SCROLL_BREATH = 15;
const TITLE_SCROLL_MARGIN_TOP = HOST_TOP_BAR_HEIGHT + TITLE_SCROLL_BREATH;

interface ObjectiveDetailsCardProps {
    control: Control<ObjectiveFormData>;
    /** Where "write the objective's title" in the measurements section brings the reader back. */
    titleCardRef: Ref<HTMLDivElement>;
    descriptionEditor: { key: number; initialValue: ReturnType<typeof htmlToSlate> };
    /** The assistant drafts for managers and HR only; absent for everyone else. */
    aiAssistant?: { isEnabled: boolean; onOpen: () => void };
    isTeam: boolean;
    ownerId?: string;
}

/** Title, description and, for an individual objective, competencies: one panel, three blocks. */
export default function ObjectiveDetailsCard({
    control,
    titleCardRef,
    descriptionEditor,
    aiAssistant,
    isTeam,
    ownerId,
}: ObjectiveDetailsCardProps) {
    const { t } = useTranslation();
    const translateError = useFormErrorMessage();

    return (
        <GlassFrame dataTestId={'objective-form-details-card'}>
            <WhitePanel>
                <div className={'flex flex-col gap-[48px]'}>
                    <div
                        ref={titleCardRef}
                        className={'flex flex-col gap-6'}
                        style={{ scrollMarginTop: `${TITLE_SCROLL_MARGIN_TOP}px` }}
                        data-testid={'objective-form-title-card'}
                    >
                        <div className={'flex flex-wrap items-center justify-between gap-3'}>
                            <SectionHeading
                                icon={'edit-edit'}
                                title={t('objectives.form.title')}
                            />

                            {aiAssistant && (
                                <GenerateObjectiveAiButton
                                    disabled={!aiAssistant.isEnabled}
                                    onClick={aiAssistant.onOpen}
                                />
                            )}
                        </div>

                        <Controller
                            name={'title'}
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextInput
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    placeholder={t('objectives.form.titlePlaceholder')}
                                    maxLength={TITLE_MAX_LENGTH}
                                    showCharacterCount
                                    error={!!fieldState.error}
                                    errorMessage={translateError(fieldState.error?.message, {
                                        max: TITLE_MAX_LENGTH,
                                    })}
                                    className={
                                        'border-obj-line rounded-[12px] bg-white/80 px-4 py-3'
                                    }
                                    dataTestId={'objective-form-title'}
                                />
                            )}
                        />
                    </div>

                    <div
                        className={'flex flex-col gap-6'}
                        data-testid={'objective-form-description-card'}
                    >
                        <SectionHeading
                            icon={'edit-note-text'}
                            title={t('objectives.form.description')}
                        />

                        <Controller
                            name={'description'}
                            control={control}
                            render={({ field }) => (
                                <RichText
                                    key={descriptionEditor.key}
                                    toolbarDensity={EDITOR_TOOLBAR_DENSITY}
                                    defaultValue={descriptionEditor.initialValue}
                                    // Slate also reports a caret move, with the document it opened
                                    // on: only an edited document is a change to the form.
                                    onChange={(value) => {
                                        if (value !== descriptionEditor.initialValue) {
                                            field.onChange(slateToHtml(value));
                                        }
                                    }}
                                    maxLength={DESCRIPTION_MAX_LENGTH}
                                    showCharacterCount
                                    placeholder={t('objectives.form.descriptionPlaceholder')}
                                    locale={i18n.language}
                                    surface={'framed'}
                                    dataTestId={'objective-form-description'}
                                />
                            )}
                        />
                    </div>

                    {!isTeam && (
                        <ObjectiveCompetenciesField
                            control={control}
                            ownerId={ownerId}
                        />
                    )}
                </div>
            </WhitePanel>
        </GlassFrame>
    );
}
